import { useEffect, useRef, useState } from "react";
import type { Stage } from "../types";

interface StageSelectorProps {
  stages: Stage[];
  activeStageId: string;
  handleStageChange: (stageId: string) => void;
}

export function StageSelector({ stages, activeStageId, handleStageChange }: StageSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeft(scrollLeft > 5);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener("scroll", checkScroll);
      }
      window.removeEventListener("resize", checkScroll);
    };
  }, [stages]);

  useEffect(() => {
    if (activeStageId && containerRef.current) {
      const timer = setTimeout(() => {
        const container = containerRef.current;
        if (!container) return;
        const activeEl = document.getElementById(`stage-filter-${activeStageId}`);
        if (activeEl) {
          const activeRect = activeEl.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const targetScrollLeft =
            container.scrollLeft +
            (activeRect.left - containerRect.left) -
            containerRect.width / 2 +
            activeRect.width / 2;

          container.scrollTo({
            left: targetScrollLeft,
            behavior: "smooth",
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeStageId, stages]);

  const handleScroll = (direction: "left" | "right") => {
    if (containerRef.current) {
      const amount = direction === "left" ? -140 : 140;
      containerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  // Map stage IDs to custom classes and active colors
  const getStageColorProps = (stageId: string) => {
    switch (stageId) {
      case "all":
        return {
          activeClass: "bg-accent text-[#222222] shadow-[0_0_16px_rgba(134,103,185,0.4)]",
          dripColor: "#8667B9",
        };

      case "nagy":
      case "stage-nagy":
      case "nagyszinpad":
        return {
          activeClass: "bg-orange text-[#222222] shadow-[0_0_16px_rgba(241,119,62,0.4)]",
          dripColor: "#F1773E",
        };
      case "kerti":
      case "stage-kerti":
      case "kerti-szinpad":
        return {
          activeClass: "bg-light-green text-[#222222] shadow-[0_0_16px_rgba(60,208,112,0.4)]",
          dripColor: "#3CD070",
        };
      case "kis":
      case "stage-kis":
      case "kisterem":
        return {
          activeClass: "bg-main text-[#222222] shadow-[0_0_16px_rgba(34,211,186,0.4)]",
          dripColor: "#22D3BA",
        };
      case "cok":
      case "stage-cok":
      case "cokxpon":
        return {
          activeClass: "bg-pink text-[#222222] shadow-[0_0_16px_rgba(247,196,255,0.4)]",
          dripColor: "#F7C4FF",
        };
      case "kerti-dj":
        return {
          activeClass: "bg-yellow text-[#222222] shadow-[0_0_16px_rgba(255,195,79,0.4)]",
          dripColor: "#FFC34F",
        };
      case "bogracs":
        return {
          activeClass: "bg-light-orange text-[#222222] shadow-[0_0_16px_rgba(249,149,50,0.4)]",
          dripColor: "#F99532",
        };
      case "tilos-caravan-studio":
        return {
          activeClass: "bg-light-purple text-[#222222] shadow-[0_0_16px_rgba(192,181,252,0.4)]",
          dripColor: "#C0B5FC",
        };
      case "merch":
        return {
          activeClass: "bg-purple text-[#222222] shadow-[0_0_16px_rgba(134,103,185,0.4)]",
          dripColor: "#8667B9",
        };
      default:
        return {
          activeClass: "bg-white text-[#222222] shadow-[0_0_16px_rgba(255,252,232,0.4)]",
          dripColor: "#FFFCE8",
        };
    }
  };

  const renderStageButton = (id: string, name: string) => {
    const isActive = activeStageId === id || (!activeStageId && id === "all");
    const colorProps = getStageColorProps(id);

    return (
      <button
        id={`stage-filter-${id}`}
        key={id}
        onClick={() => handleStageChange(id)}
        className={`flex-shrink-0 px-3.5 py-2 rounded-[20px] border text-[11.5px] font-bold uppercase tracking-wider font-['Unbounded'] cursor-pointer relative transition-all duration-300 ${
          isActive
            ? `${colorProps.activeClass} border-transparent`
            : "bg-[#222222]/60 border-white/5 hover:border-white/15 text-muted hover:text-white"
        }`}
        type="button"
        role="button"
        aria-label={id === "all" ? "Minden színpad mutatása" : `Szűrés színpadra: ${name}`}
      >
        <span className="flex items-center gap-2">
          {/* Colored Dot Indicator */}
          <span
            className={`w-2 h-2 rounded-full border transition-all duration-300 ${isActive ? "border-black/25" : "border-white/10"}`}
            style={{ backgroundColor: colorProps.dripColor }}
          />
          <span>{name}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="mb-4">
      <div className="font-['Unbounded'] text-[8px] font-extrabold uppercase tracking-widest text-muted mb-1.5 pl-0.5 hidden">
        Színpad Szűrés
      </div>

      <div className="relative group">
        {/* Left Fade Gradient */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10 transition-opacity duration-300 ${
            showLeft ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* Right Fade Gradient */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10 transition-opacity duration-300 ${
            showRight ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* Left Arrow Button */}
        <button
          onClick={() => handleScroll("left")}
          className={`absolute left-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full border border-white/10 bg-[#222222]/90 text-accent flex items-center justify-center active:scale-90 shadow-[0_0_12px_rgba(0,0,0,0.6)] hover:bg-[#333333] hover:border-white/20 transition-all duration-300 ${
            showLeft
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-75 pointer-events-none"
          }`}
          type="button"
          aria-label="Görgetés balra"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Right Arrow Button */}
        <button
          onClick={() => handleScroll("right")}
          className={`absolute right-1 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full border border-white/10 bg-[#222222]/90 text-accent flex items-center justify-center active:scale-90 shadow-[0_0_12px_rgba(0,0,0,0.6)] hover:bg-[#333333] hover:border-white/20 transition-all duration-300 ${
            showRight
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-75 pointer-events-none"
          }`}
          type="button"
          aria-label="Görgetés jobbra"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div
          ref={containerRef}
          className="flex gap-2 items-center overflow-x-auto p-0.5 stage-row scrollbar-none scroll-smooth"
        >
          {renderStageButton("all", "Minden")}
          {stages.map((stage) => renderStageButton(stage.id, stage.name))}
        </div>
      </div>
    </div>
  );
}
